import React from 'react';

import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {phraseToCamelCase} from 'util/NameUtil';

export interface ActionFunctionButtonProps {
  tooltip: string | JSX.Element;
  tooltipDisabled?: boolean;
  buttonStyle?: ButtonStyle;
  disabled: boolean;
  icon?: BenchlingIconName | JSX.Element;
  iconAltText?: string;
  onClick: () => void;
  buttonText?: string;
}

/**
 * Simple wrapper component to render an action button in the toolbar of a structured table.
 */
export default function ActionFunctionButton({
  tooltip,
  tooltipDisabled,
  buttonStyle = ButtonStyle.TERTIARY_GRAY,
  disabled,
  icon,
  iconAltText,
  buttonText,
  onClick,
}: ActionFunctionButtonProps): JSX.Element {
  return (
    <ActionFunctionTooltip tooltip={tooltip} isDisabled={tooltipDisabled || !tooltip}>
      <span className="u-mini-spacing-left">
        <Button
          buttonStyle={buttonStyle}
          disabled={disabled}
          icon={icon}
          iconAltText={iconAltText}
          text={buttonText}
          onClick={onClick}
          size={ButtonSize.MINI}
          dataTestAttrs={{
            component: 'ActionFunctionButton',
            element: 'button',
            key: phraseToCamelCase(buttonText || iconAltText),
          }}
        />
      </span>
    </ActionFunctionTooltip>
  );
}

function isJsxElement(tooltip: string | JSX.Element): tooltip is JSX.Element {
  return React.isValidElement(tooltip);
}

function ActionFunctionTooltip({
  children,
  tooltip,
  isDisabled,
}: {
  tooltip: string | JSX.Element;
  isDisabled: boolean;
  children: JSX.Element;
}): JSX.Element {
  return isJsxElement(tooltip) ? (
    <Popover
      interaction={FloatingInteraction.HOVER}
      placement={FloatingPlacement.TOP_END}
      renderContent={() => tooltip}
      isDisabled={isDisabled}
    >
      {children}
    </Popover>
  ) : (
    <Tooltip title={tooltip} disabled={isDisabled}>
      {children}
    </Tooltip>
  );
}
